import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Table } from 'design-system-web';
import { AdminPage } from 'components';
import { H3 } from 'components/typography';

import type { InspectionsChannel } from '../types/channel';
import styles from './InspectionsTemplateLibrary.scss';
import {
  type TemplateLibraryTableRow,
  mapTemplateToTableRow,
} from './templateLibraryTableConfig';
import { useTemplateLibraryTable } from '../hooks/useTemplateLibraryTable';
import { useCustomizeInspectionTemplate } from '../hooks/useCustomizeInspectionTemplate';
import { useInspectionTemplateHideEnabled } from '../hooks/useInspectionTemplateHideEnabled';
import { useInspectionTemplateVisibility } from '../hooks/useInspectionTemplateVisibility';
import {
  useToggleInspectionTemplateVisibility,
  UNDO_TOAST_DURATION_MS,
} from '../hooks/useToggleInspectionTemplateVisibility';
import { useTemplateLibraryUndoToast } from '../hooks/useTemplateLibraryUndoToast';
import { UndoToast } from '../components/UndoToast';

type Props = {
  channel: InspectionsChannel;
  /** Channel admin route prefix (e.g. match.path from portal), without trailing slash */
  channelBasePath: string;
};

export function InspectionsTemplateLibrary({
  channel,
  channelBasePath,
}: Props) {
  const { t } = useTranslation();
  const { customizeTemplate } = useCustomizeInspectionTemplate({
    channel,
    channelBasePath,
  });
  const hideTemplateEnabled = useInspectionTemplateHideEnabled(channel?._id);

  const {
    templates,
    templatesById,
    loading,
    error,
    called,
    handleVisibilityChanged,
  } = useInspectionTemplateVisibility({ channel, hideTemplateEnabled });

  const { undoToast, closeUndoToast, showUndoToast } =
    useTemplateLibraryUndoToast({ handleVisibilityChanged });

  const { toggleTemplateVisibility } = useToggleInspectionTemplateVisibility({
    channel,
    onVisibilityChanged: handleVisibilityChanged,
  });

  const rows = useMemo(
    () => templates.map(mapTemplateToTableRow),
    [templates]
  );

  const handleCustomize = useCallback(
    (row: TemplateLibraryTableRow) => {
      const template = templatesById.get(row.id);

      if (!template) {
        return;
      }

      customizeTemplate(template);
    },
    [templatesById, customizeTemplate]
  );

  const handleToggleVisibility = useCallback(
    async (row: TemplateLibraryTableRow) => {
      const template = templatesById.get(row.id);

      if (!template) {
        return;
      }

      const completion = await toggleTemplateVisibility(template);

      // `undefined` means nothing completed (cancelled, de-duped, or failed) —
      // only surface the undo toast when a change actually landed.
      if (completion) {
        showUndoToast(completion);
      }
    },
    [templatesById, toggleTemplateVisibility, showUndoToast]
  );

  const isChildChannel = Boolean(channel.parent?._id);

  const { columns, tableKey, rowActions, filterTemplatesByName } =
    useTemplateLibraryTable({
      hideTemplateEnabled,
      isChildChannel,
      onCustomize: handleCustomize,
      onToggleVisibility: handleToggleVisibility,
    });

  if (error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.templateLibrary}>
          <div className={styles.header}>
            <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
          </div>
          <p>{t('web.admin.inspections.templateLibrary.error')}</p>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.templateLibrary}>
        <div className={styles.header}>
          <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
        </div>
        <Table<TemplateLibraryTableRow>
          columns={columns}
          data={rows}
          isLoading={loading || !called}
          pagination="client"
          defaultPageSize={25}
          rowActions={rowActions}
          rowActionsLabel={t(
            'web.admin.inspections.templateLibrary.actions.label'
          )}
          emptyMessage={t('web.admin.inspections.templateLibrary.emptyState')}
          hasKeywordFilter
          keywordFilterLabel={t(
            'web.admin.inspections.templateLibrary.searchLabel'
          )}
          customKeywordFilterFn={filterTemplatesByName}
          getRowId={row => row.id}
          showColumnVisibility
          // `getMergedColumnOrder` (design-system-web) drops a column key from the
          // persisted order once it disappears from `columns`, then re-appends it
          // at the END the next time it reappears — so if the visibility column
          // shared a cache key with the base table, toggling the hide-template
          // flag off then on would push it to the end every time. Keying the two
          // column sets separately keeps each one's persisted order isolated, so
          // the flag can flip without corrupting the other's column order.
          //
          // `key` forces a remount (not just a prop change) when the flag flips,
          // since usePersistedSetting/useLocalStorage only reads localStorage in
          // its useState initializer — that won't re-run just because the
          // `tableKey` prop changed on an already-mounted Table.
          key={tableKey}
          tableKey={tableKey}
        />
      </div>
      <UndoToast
        isOpen={undoToast.isOpen}
        message={undoToast.message}
        actionLabel={undoToast.actionLabel}
        onAction={undoToast.onAction}
        onDismiss={closeUndoToast}
        duration={UNDO_TOAST_DURATION_MS}
      />
    </AdminPage>
  );
}
